import { useState, useEffect, useMemo, useCallback } from 'react';
import { walletService } from '../../_services/wallet.service';

// Some gift cards come back with a currency code ("GBP") and others with the
// symbol ("£"); the card art shows whatever it's given, so "GBP100" sat next
// to "£182". Always the symbol.
const SYMBOLS = { GBP: '£', EUR: '€', USD: '$', AUD: '$', CAD: '$', NZD: '$' };
export const currencySymbol = (c) => {
  const v = String(c || '').trim();
  if (!v) return '£';
  return SYMBOLS[v.toUpperCase()] || v;
};

// Normalize a gift-card instance into a generic WalletItem. Loyalty cards
// arrive the same way (a second fetch merged into this same array) — the
// wallet groups by salon, so they slot in beside gift cards.
export const normalizeGiftCard = (gc) => ({
  kind: 'gift-card',
  id: gc.id,
  salonId: gc.salonId,
  salon: {
    name: gc.salonName,
    logo: gc.salonLogo || null,
    slug: gc.salonSlug || gc.salonId,
    currency: currencySymbol(gc.currency),
  },
  code: gc.code,
  voucherName: gc.voucherName,
  designId: gc.designId,
  faceValue: gc.faceValue,
  balance: gc.balance,
  status: gc.status,
  purchasedAt: gc.purchasedAt,
  expiresAt: gc.expiresAt,
  currency: currencySymbol(gc.currency),
});

// Normalize a loyalty card (v2 loyalty/wallet shape) into a WalletItem.
// Progress lives under `state` — deliberately NOT a top-level `balance`, so
// gift-card-only consumers (booking apply, ownership cue) skip these.
export const normalizeLoyaltyCard = (lc) => ({
  kind: 'loyalty',
  id: `loyalty-${lc.schemeId}`,
  salonId: lc.salonId,
  salon: {
    name: lc.salonName || 'Your salon',
    logo: lc.salonLogo || null,
    slug: lc.salonSlug || lc.salonId,
    currency: '£',
  },
  schemeId: lc.schemeId,
  design: lc.design || {},
  scheme: lc.scheme || {},
  state: lc.state || {},
});

// useWallet — the signed-in customer's wallet. No args = everything (grouped by
// salon, for the wallet screen). `{ salonId }` = just that salon's items (for
// the salon-page "you already own one" cue and booking redemption).
const useWallet = ({ salonId } = {}) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  const [reloadKey, setReloadKey] = useState(0);
  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  // Optimistically allocate a design on a card the user holds, persisting via
  // the service (mock-first).
  const setDesign = useCallback((cardId, designId) => {
    setItems((prev) => prev.map((i) => (i.id === cardId ? { ...i, designId } : i)));
    walletService.setGiftCardDesign(cardId, designId);
  }, []);

  // Append a just-claimed card to the wallet (optimistic; the service has already
  // bound it). Guards against duplicates so a later refetch can't double it.
  const addCard = useCallback((walletItem) => {
    setItems((prev) => (prev.some((i) => i.id === walletItem.id) ? prev : [walletItem, ...prev]));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Both kinds fetched together; either failing alone never empties the
    // other (allSettled), and a total failure surfaces the gift-card error.
    Promise.allSettled([
      walletService.getMyGiftCards(),
      walletService.getMyLoyaltyCards(),
    ]).then(([gift, loyalty]) => {
      if (!alive) return;
      const giftItems = gift.status === 'fulfilled' ? (gift.value || []).map(normalizeGiftCard) : [];
      const loyaltyItems = loyalty.status === 'fulfilled' ? (loyalty.value || []).map(normalizeLoyaltyCard) : [];
      if (gift.status === 'rejected' && loyalty.status === 'rejected') {
        setError(gift.reason);
      }
      setItems([...giftItems, ...loyaltyItems]);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [reloadKey]);

  const filtered = useMemo(
    () => (salonId ? items.filter((i) => i.salonId === salonId) : items),
    [items, salonId]
  );

  // Group by salon, preserving first-seen order.
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((item) => {
      if (!map.has(item.salonId)) {
        map.set(item.salonId, { salonId: item.salonId, salon: item.salon, items: [] });
      }
      map.get(item.salonId).items.push(item);
    });
    return Array.from(map.values());
  }, [filtered]);

  return { loading, items: filtered, groups, error, setDesign, addCard, refetch };
};

export default useWallet;
